import { useEffect, useState } from 'react'
import {
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  dateColumn,
  type ColumnDef,
} from '@/components/ui'
import { customerApi } from '../lib/api'

interface OposRow {
  DocEntry: number
  DocNum?: number
  DocDate?: string
  DocDueDate?: string
  DocTotal: number
  PaidToDate?: number
  DocCurrency?: string
  DocType?: string
  CardCode: string
}

function fmtMoney(n: number, cur?: string) {
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2 })} ${cur ?? 'EUR'}`
}

export function CustomerOposPage() {
  const [rows, setRows] = useState<OposRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    customerApi.get<{ data: OposRow[]; total: number }>('/sap/opos')
      .then((res) => { setRows(res.data?.data ?? []); setTotal(res.data?.total ?? 0) })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  const columns: ColumnDef<OposRow, unknown>[] = [
    {
      accessorKey: 'DocNum',
      header: 'Nr.',
      size: 100,
      cell: ({ row }) => row.original.DocNum ?? row.original.DocEntry,
    },
    dateColumn<OposRow>('DocDate', 'Datum'),
    dateColumn<OposRow>('DocDueDate', 'Fällig'),
    {
      accessorKey: 'DocTotal',
      header: 'Betrag',
      cell: ({ row }) => (
        <span className="block text-right tabular-nums">
          {fmtMoney(row.original.DocTotal, row.original.DocCurrency)}
        </span>
      ),
    },
    {
      accessorKey: 'PaidToDate',
      header: 'Bezahlt',
      cell: ({ row }) => (
        <span className="block text-right tabular-nums">
          {fmtMoney(row.original.PaidToDate ?? 0, row.original.DocCurrency)}
        </span>
      ),
    },
    {
      id: 'open',
      header: 'Offen',
      cell: ({ row }) => {
        const open = row.original.DocTotal - (row.original.PaidToDate ?? 0)
        return (
          <span className="block text-right tabular-nums font-medium">
            {fmtMoney(open, row.original.DocCurrency)}
          </span>
        )
      },
    },
  ]

  return (
    <div>
      <PageHeader title="Offene Posten" description="Noch nicht ausgeglichene Rechnungen." />
      {loading && <LoadingState />}
      {error && <ErrorState details={error} />}
      {!loading && !error && (
        <>
          <Card className="mb-4">
            <Card.Body>
              <div className="text-body text-gray-600">
                Gesamtsumme offen: <strong className="text-body-lg text-gray-900 tabular-nums">{fmtMoney(total)}</strong>
              </div>
            </Card.Body>
          </Card>
          {rows.length === 0 ? (
            <EmptyState title="Keine offenen Posten" description="Alle Rechnungen sind ausgeglichen." />
          ) : (
            <DataTable<OposRow>
              data={rows}
              columns={columns}
              getRowId={(r) => String(r.DocEntry)}
              pagination={{ pageSize: 25 }}
            />
          )}
        </>
      )}
    </div>
  )
}
